// Global Instructions Rule Applied!
//
// The in-app user-guide drawer.
//
// Chrome is the house drawer chrome (EditColumnsDrawer / AggregationsButton /
// CustomizeReportDrawer): right-hand placement, 40px gradient header, the
// hand-rolled white close button, no AntD footer.
//
// The one deliberate difference is `mask={false}`. A guide is read WHILE you
// work — copying the connector URL, revoking a grant, moving between tabs —
// so the page behind must stay interactive. A masked drawer would make the
// guide modal and defeat the point of it being a drawer at all.

import React from 'react'
import { Button as AntButton, Drawer, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { portalGradients } from '../../../portal-configs/colors.js'
import { HelpArticleBody } from './HelpArticleBody.tsx'
import { useHelpArticle } from '../../hooks/useHelpArticle.ts'

export interface HelpDrawerProps {
  articleKey: string
  open: boolean
  onClose: () => void
}

export function HelpDrawer({ articleKey, open, onClose }: HelpDrawerProps) {
  // Nothing is fetched until the drawer is actually opened, and the service
  // caches per article for the session.
  const { article, loading, error } = useHelpArticle(articleKey, open)

  return (
    <Drawer
      title={
        <div className='flex w-full min-w-0 items-center justify-between gap-2'>
          <span className='min-w-0 flex-1 truncate text-base font-semibold leading-snug text-white'>
            {article?.title || 'User guide'}
          </span>
          <Tooltip title='Close'>
            <AntButton
              type='default'
              size='small'
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={onClose}
              aria-label='Close'
              className='flex-shrink-0 bg-white/20 !border-white/40 !text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
              style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
            />
          </Tooltip>
        </div>
      }
      placement='right'
      width={480}
      open={open}
      onClose={onClose}
      closable={false}
      mask={false}
      classNames={{
        header:
          'shadow-lg !border-b-0 !h-10 !min-h-10 !max-h-10 !p-0 !px-4 flex items-center [&_.ant-drawer-header-title]:!m-0 [&_.ant-drawer-header-title]:!w-full [&_.ant-drawer-header-title]:!flex-1 [&_.ant-drawer-header-title]:!flex [&_.ant-drawer-header-title]:min-w-0 [&_.ant-drawer-header-title]:items-center'
      }}
      styles={{
        header: {
          background: portalGradients.navbar,
          height: 40,
          minHeight: 40,
          maxHeight: 40,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: 'none',
          boxSizing: 'border-box'
        },
        content: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflowY: 'auto',
          padding: '8px 16px 16px'
        }
      }}
    >
      {article?.subtitle ? (
        <p className='mb-2 mt-0 text-xs leading-snug text-gray-500'>{article.subtitle}</p>
      ) : null}
      <HelpArticleBody article={article} loading={loading} error={error} />
    </Drawer>
  )
}
